// Deterministic furniture categorization.
//
// KEEP IN SYNC with supabase/functions/_shared/furniture-category.ts
// (edge functions can't import from src/, so the rule table is duplicated
// verbatim in both files).
//
// Categories are derived from signals we control — the search query we issued
// for each furniture slot, plus head nouns in the product title. No model call
// is involved; the AI hint is only a last-resort tiebreak.

export const FURNITURE_CATEGORIES = [
  "Beds",
  "Seating",
  "Tables",
  "Storage",
  "Lighting",
  "Decor",
  "Rugs",
  "Other",
] as const;

export type FurnitureCategory = (typeof FURNITURE_CATEGORIES)[number];

// Head-noun rules. Order within the array is irrelevant for matching — we pick
// the LAST match in the text, so "Bedside Table Lamp" resolves to Lighting.
const KEYWORDS: Array<[RegExp, FurnitureCategory]> = [
  [/\b(bed frame|platform bed|upholstered bed|canopy bed|bunk bed|daybed|headboard|mattress|bed)\b/gi, "Beds"],
  [/\b(sofa|sectional|couch|loveseat|armchair|accent chair|dining chair|recliner|chair|stool|bench|ottoman|settee|pouf)\b/gi, "Seating"],
  [/\b(coffee table|side table|end table|dining table|console table|bedside table|nightstand|writing desk|desk|bar cart|console|credenza|table)\b/gi, "Tables"],
  [/\b(tv stand|media console|shelf|shelving|shelves|bookshelf|bookcase|cabinet|dresser|chest of drawers|storage|wardrobe|sideboard|buffet|hutch|armoire|basket|storage bin)\b/gi, "Storage"],
  [/\b(floor lamp|table lamp|desk lamp|lamp|sconce|pendant|chandelier|flush mount|light fixture|lighting|lantern)\b/gi, "Lighting"],
  [/\b(area rug|rug|carpet|runner)\b/gi, "Rugs"],
  [/\b(mirror|curtain|curtains|drapes|planter|plant|vase|artwork|wall art|print|poster|throw pillow|pillow|cushion|throw|blanket|candle|tray|clock|sculpture|decor)\b/gi, "Decor"],
];

/** Returns the category whose head noun appears LAST in the text, or null. */
function matchLastHeadNoun(text?: string | null): FurnitureCategory | null {
  if (!text) return null;
  let best: FurnitureCategory | null = null;
  let bestIndex = -1;
  for (const [re, cat] of KEYWORDS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    let last = -1;
    while ((m = re.exec(text)) !== null) {
      last = m.index;
      if (m.index === re.lastIndex) re.lastIndex++;
    }
    if (last > bestIndex) {
      bestIndex = last;
      best = cat;
    }
  }
  return best;
}

/** Category implied by the furniture slot we searched for. */
export function categorizeFromQuery(slotName?: string, searchQuery?: string): FurnitureCategory | null {
  return matchLastHeadNoun(slotName) ?? matchLastHeadNoun(searchQuery);
}

/** Category implied by the retailer's product title. */
export function categorizeFromTitle(title?: string): FurnitureCategory | null {
  if (!title) return null;
  // Retail titles lead with the actual piece and then pile on keyword-stuffed
  // usage phrases ("...Wooden Bench for Bedroom End of Bed, Dining Room"), so
  // matching the whole string lands on the wrong noun. Read the leading clause
  // first and only fall back to the full title if it names nothing.
  const head = title.split(/[,|/()\u2013\u2014]|\s-\s/)[0];
  return matchLastHeadNoun(head) ?? matchLastHeadNoun(title);
}

// Listings that are parts, plans or accessories rather than the piece itself.
// These rank well on shopping searches but are never a valid recommendation.
const JUNK_PATTERNS: RegExp[] = [
  /\b(plans?|blueprints?|printable|template|tutorial|woodworking project)\b/i,
  /\b(pdf|digital download|instant download|svg|cricut)\b/i,
  /\bdiy\b/i,
  /\b(hardware|leg|slat|replacement|repair|assembly)\s+kits?\b/i,
  /\bno hardware\b/i,
  /\bslats?\s*(?:[-\u2013,:]|$)/i,
  /\b(replacement|spare)\s+(parts?|cushions?|covers?|slats?|legs?)\b/i,
  /\b(parts?|legs?|cover|cushion cover|frame)\s+only\b/i,
  /\b(swatch|swatches|fabric sample|paint sample|colou?r sample)\b/i,
  /\bunfinished\s+(kit|blank)\b/i,
];

/** True when a listing is a part, plan or accessory rather than the furniture. */
export function isJunkListing(title?: string | null): boolean {
  if (typeof title !== "string" || !title.trim()) return false;
  return JUNK_PATTERNS.some((re) => re.test(title));
}

/**
 * Deterministic resolution order:
 *  1. slot query rules (the terms we chose — controlled vocabulary)
 *  2. product-title override when the title clearly names a different piece
 *  3. AI hint (weakest tiebreak)
 *  4. "Other"
 */
export function resolveCategory(
  name: string,
  hint?: string,
  productTitle?: string,
  searchQuery?: string,
): FurnitureCategory {
  const fromQuery = categorizeFromQuery(name, searchQuery);
  const fromTitle = categorizeFromTitle(productTitle);

  if (fromQuery && fromTitle && fromTitle !== fromQuery) return fromTitle;
  if (fromQuery) return fromQuery;
  if (fromTitle) return fromTitle;
  if (hint && (FURNITURE_CATEGORIES as readonly string[]).includes(hint)) {
    return hint as FurnitureCategory;
  }
  return "Other";
}
